<script lang="ts">
    import { untrack } from 'svelte';
    import type { Variation } from '@shared/types/types.ts';
    import AvailabilityComponent from '@tile/components/availability/Availability.svelte';
    import Card from '@tile/components/card/Card.svelte';
    import ColorThumbs from '@tile/components/color-thumbs/ColorThumbs.svelte';
    import EekLabel from '@tile/components/eek/EekLabel.svelte';
    import OttoUp from '@tile/components/otto-up/OttoUp.svelte';
    import PricingItem from '@tile/components/pricing-item/PricingItem.svelte';
    import ProductBrand from '@tile/components/product-brand/ProductBrand.svelte';
    import ProductHeader from '@tile/components/product-header/ProductHeader.svelte';
    import ProductLink from '@tile/components/product-link/ProductLink.svelte';
    import ProductTitle from '@tile/components/product-title/ProductTitle.svelte';
    import RadioEquipmentRegulation from '@tile/components/radio-equipment-regulation/RadioEquipmentRegulation.svelte';
    import TopInfo from '@tile/components/top-info/TopInfo.svelte';
    import { sortedColorsWithBestVariationFirst } from '@tilelist/domain/feature-visibility/ColorSorting.ts';
    import { resolveDeliveryText } from '@tilelist/layouts/dispatchers/delivery-dispatcher/resolveDeliveryText.ts';
    import { stripLieferbarPrefix } from '@tilelist/layouts/dispatchers/delivery-dispatcher/stripLieferbarPrefix.ts';
    import RatingDispatcher from '@tilelist/layouts/dispatchers/rating-dispatcher/RatingDispatcher.svelte';
    import ProductImageE3933 from '@tilelist/layouts/experiments/e3933/product-image/ProductImage.svelte';
    import { createTileLayout } from '@tilelist/layouts/tile-layout/TileLayout.ts';

    interface Props {
        variation: Variation;
        position: number;
        onTileClick?: (event: Event) => void;
    }

    let { variation, onTileClick }: Props = $props();

    const layout = $derived(createTileLayout(variation, true, true));
    /*                                                                                                */
    const variationSnapshot = untrack(() => $state.snapshot(variation));
    const colors = sortedColorsWithBestVariationFirst(variationSnapshot.colors, variationSnapshot.variationId);
    const deliveryText = $derived(stripLieferbarPrefix(resolveDeliveryText(variation.availability, false)));
    const hasColors = !!(colors && colors.length > 1);
</script>

<Card data-jarvis="reptile-sheet-product-tile" data-variation-id={variation.variationId} fullHeight size="50">
    {#snippet primaryAction()}
        <ProductLink id={`sheet-link-${variation.variationId}`} link={layout.link} onClick={onTileClick} />
    {/snippet}

    <div class="reptile-sheet-tile__image">
        <ProductImageE3933
            image={layout.primaryImage}
            variationId={variation.variationId}
            imageLoadingOptions={{ loading: 'lazy', fetchpriority: 'auto' }}
            aspectRatio="portrait"
        />
    </div>

    <div class="reptile-sheet-tile__content">
        <ProductHeader>
            <ProductBrand brand={layout.title.brand} />
            <ProductTitle name={layout.title.display} />
            <TopInfo infos={variation.topInfos} multipleSizes={layout.showMultipleSizes && !!variation.multipleSizes} />
            <RadioEquipmentRegulation radioEquipmentRegulation={variation.radioEquipmentRegulation} />
            <RatingDispatcher customerReviews={variation.customerReviews} showRating={true} />
        </ProductHeader>

        <EekLabel labels={variation.energyLabels} asRow />

        <PricingItem price={variation.price} variationId={variation.variationId} showBenefitTag={false} />

        <div class="reptile-sheet-tile__purchase-info">
            {#if deliveryText || variation.ottoUp}
                <div class="reptile-sheet-tile__row">
                    <AvailabilityComponent text={deliveryText} />
                    <OttoUp show={variation.ottoUp} />
                </div>
            {/if}

            {#if hasColors}
                <div class="reptile-sheet-tile__row">
                    <ColorThumbs {colors} />
                </div>
            {/if}
        </div>
    </div>
</Card>

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@shared/styles/mixins' as reptileMixins;

    .reptile-sheet-tile {
        &__image {
            position: relative;
        }

        &__content,
        &__purchase-info {
            display: flex;
            flex-direction: column;
        }

        &__content {
            gap: tokens.$oc-semantic-spacing-absolute-100;
            padding: tokens.$oc-semantic-spacing-absolute-75;
        }

        &__purchase-info {
            gap: tokens.$oc-semantic-spacing-absolute-50;
        }

        &__row {
            @include reptileMixins.row-space-between;

            gap: tokens.$oc-semantic-spacing-absolute-50;
            min-height: tokens.$oc-semantic-spacing-absolute-150;
        }
    }
</style>
